import { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { Button } from './button'
import { ArrowRight, Check } from 'lucide-react'

export interface ServiceCardProps {
  title: string
  description: string
  icon?: ReactNode
  price?: string
  features?: string[]
  ctaText?: string
  onCtaClick?: () => void
  highlighted?: boolean
  className?: string
}

export function ServiceCard({
  title,
  description,
  icon,
  price,
  features = [],
  ctaText = 'Learn More',
  onCtaClick,
  highlighted = false,
  className,
}: ServiceCardProps) {
  return (
    <div
      className={cn(
        'card p-6 relative flex flex-col',
        highlighted && 'border-primary/50 ring-1 ring-primary/30 shadow-glow',
        className
      )}
    >
      {highlighted && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="badge badge-primary">Most Popular</span>
        </div>
      )}

      {/* Icon */}
      {icon && (
        <div className="mb-4 text-primary">
          {icon}
        </div>
      )}

      {/* Title */}
      <h3 className="text-xl font-semibold text-card-foreground mb-2">
        {title}
      </h3>

      {/* Description */}
      <p className="text-muted-foreground mb-4 flex-grow">
        {description}
      </p>

      {/* Price */}
      {price && price.trim() !== '' && (
        <div className="mb-4">
          <div className="text-3xl font-bold text-card-foreground">{price}</div>
        </div>
      )}

      {/* Features */}
      {features.length > 0 && (
        <ul className="space-y-2 mb-6 flex-grow">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start gap-2 text-sm text-card-foreground">
              <Check className="h-4 w-4 text-success mt-0.5 flex-shrink-0" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      )}

      {/* CTA */}
      <Button
        onClick={onCtaClick}
        variant={highlighted ? 'default' : 'outline'}
        className="w-full"
      >
        {ctaText}
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </div>
  )
}

// Service Grid Component
export interface ServiceGridProps {
  children: ReactNode
  columns?: 1 | 2 | 3
  className?: string
}

export function ServiceGrid({ children, columns = 3, className }: ServiceGridProps) {
  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
  }

  return (
    <div className={cn('grid gap-6', gridCols[columns], className)}>
      {children}
    </div>
  )
}

// Feature Card - For highlighting key features
export interface FeatureCardProps {
  title: string
  description: string
  icon: ReactNode
  className?: string
}

export function FeatureCard({ title, description, icon, className }: FeatureCardProps) {
  return (
    <div className={cn('card p-6 text-center hover-lift', className)}>
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 text-primary mb-4">
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-card-foreground mb-2">
        {title}
      </h3>
      <p className="text-muted-foreground text-sm">
        {description}
      </p>
    </div>
  )
}

// Testimonial Card
export interface TestimonialCardProps {
  quote: string
  author: string
  role?: string
  avatar?: string
  className?: string
}

export function TestimonialCard({ quote, author, role, avatar, className }: TestimonialCardProps) {
  return (
    <div className={cn('card p-6', className)}>
      <blockquote className="text-card-foreground mb-4">
        "{quote}"
      </blockquote>
      <div className="flex items-center gap-3">
        {avatar && (
          <img
            src={avatar}
            alt={author}
            className="w-10 h-10 rounded-full object-cover"
          />
        )}
        <div>
          <div className="font-semibold text-card-foreground">{author}</div>
          {role && (
            <div className="text-sm text-muted-foreground">{role}</div>
          )}
        </div>
      </div>
    </div>
  )
}
